import { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Platform Analytics Types
export interface PlatformAnalyticsSummary {
  totalCompanies: number;
  totalEmployees: number;
  totalUsers: number;
  avgRevenueScore: number;
}

export interface RevenueDistribution {
  range: string;
  count: number;
  percentage: number;
}

export interface AiAdoptionDistribution {
  stage: string;
  count: number;
  percentage: number;
}

export interface EmployeeDistribution {
  range: string;
  count: number;
  percentage: number;
}

export interface CompanyScatterData {
  id: string;
  companyName: string;
  employeeCount: number;
  revenueRange: string;
  revenueScore: number;
  aiAdoptionStage: string | null;
}

export interface PlatformAnalytics {
  summary: PlatformAnalyticsSummary;
  revenueDistribution: RevenueDistribution[];
  aiAdoptionDistribution: AiAdoptionDistribution[];
  employeeDistribution: EmployeeDistribution[];
  companyScatterData: CompanyScatterData[];
}

interface UsePlatformAnalyticsResult {
  data: PlatformAnalytics | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook for fetching platform-wide analytics data
 * Requires authentication (super_admin role only)
 */
export function usePlatformAnalytics(): UsePlatformAnalyticsResult {
  const [data, setData] = useState<PlatformAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch platform analytics' }));
        throw new Error(errorData.error || 'Failed to fetch platform analytics');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching platform analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { data, loading, error, refetch: fetchData };
}
